import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaClock, FaDumbbell, FaArrowLeft, FaCheckCircle, FaExclamationTriangle, FaPlay, FaCopy, FaCalendarPlus, FaEdit, FaTrash } from 'react-icons/fa';
import { cloneProgram, deleteUserProgram } from '../../services/fitnessService';
import { cloneCoachProgram } from '../../services/coachFitnessService';
import { getMyCoaches, unsubscribeFromCoach } from '../../services/nutritionService';
import ScheduleModal from './ScheduleModal';
import ConfirmModal from '../ConfirmModal';
import toast from 'react-hot-toast';
import SEO from '../SEO';
import { absoluteUrl, buildBreadcrumbs } from '../../utils/seoConfig';
import './ProgramPage.css';

const resolveProgramRefForMatch = (ref) => {
  if (ref == null) return null;
  if (typeof ref === 'string') return ref;
  return ref.programId || (ref._id != null ? String(ref._id) : null);
};

const ProgramPage = () => {
  const { programId } = useParams();
  const navigate = useNavigate();
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [coachingAssignment, setCoachingAssignment] = useState(null);

  useEffect(() => {
    fetchProgram();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programId]);

  useEffect(() => {
    if (!program || !localStorage.getItem('token')) {
      setCoachingAssignment(null);
      return undefined;
    }
    let cancelled = false;
    const slug = program.programId || programId;
    (async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/fitness/my-assignments`,
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        );
        if (!res.ok || cancelled) return;
        const list = await res.json();
        const found = (list || []).find((a) => {
          if (!a.assignedBy || !a.programId) return false;
          const pid = resolveProgramRefForMatch(a.programId);
          return pid === slug || String(a.programId?._id) === String(slug);
        });
        if (!cancelled) setCoachingAssignment(found || null);
      } catch {
        if (!cancelled) setCoachingAssignment(null);
      }
    })();
    return () => { cancelled = true; };
  }, [program, programId]);

  const fetchProgram = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/fitness/programs/${programId}`,
        token ? { headers: { Authorization: `Bearer ${token}` } } : undefined
      );
      if (!response.ok) throw new Error('Failed to fetch program');
      const data = await response.json();
      setProgram(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'lower-body': return '#10b981';
      case 'upper-body': return '#3b82f6';
      case 'full-body': return '#8b5cf6';
      case 'core': return '#f59e0b';
      default: return '#6b7280';
    }
  };

  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const isCoach = user?.isAffiliate && user?.fitnessEnabled;
  const [showSchedule, setShowSchedule] = useState(false);
  const [unsubscribeModalOpen, setUnsubscribeModalOpen] = useState(false);
  const [unsubscribeLoading, setUnsubscribeLoading] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [cascadeWorkouts, setCascadeWorkouts] = useState(false);
  const [cascadeExercises, setCascadeExercises] = useState(false);

  const resolveCoachClientIdForAssignment = async (assignment) => {
    if (assignment?.coachClientId) {
      return typeof assignment.coachClientId === 'object'
        ? assignment.coachClientId._id
        : assignment.coachClientId;
    }
    if (!assignment?.assignedBy) return null;
    const coaches = await getMyCoaches();
    const fitness = (coaches || []).filter(c => ['fitness', 'both'].includes(c.coachType));
    const aid = typeof assignment.assignedBy === 'object'
      ? assignment.assignedBy._id
      : assignment.assignedBy;
    const match = fitness.find(c => String(c.coachId?._id) === String(aid));
    return match?._id || null;
  };

  const handleHideCoachAssignment = async () => {
    if (!coachingAssignment?._id) return;
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/fitness/my-schedule/${coachingAssignment._id}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ hiddenFromClient: true })
        }
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'Failed to hide');
      toast.success('Hidden from My Workouts');
      setCoachingAssignment(null);
    } catch (err) {
      toast.error(err.message || 'Failed to hide');
    }
  };

  const runUnsubscribeCoachFromProgram = async () => {
    if (!coachingAssignment) {
      setUnsubscribeModalOpen(false);
      return;
    }
    const coachName = coachingAssignment.assignedBy?.name || 'your coach';
    setUnsubscribeLoading(true);
    try {
      const relId = await resolveCoachClientIdForAssignment(coachingAssignment);
      if (!relId) {
        toast.error('Could not find your coach connection. Use My Workouts → From Your Coach to unsubscribe.');
        setUnsubscribeModalOpen(false);
        return;
      }
      const data = await unsubscribeFromCoach(relId);
      toast.success(`You are no longer connected with ${coachName}.`);
      if (data.stripe?.attempted && !data.stripe?.cancelled && data.stripe?.error) {
        toast.error(`Stripe could not cancel billing: ${data.stripe.error}`);
      }
      setCoachingAssignment(null);
      setUnsubscribeModalOpen(false);
    } catch (err) {
      toast.error(err.message || 'Failed to unsubscribe');
    } finally {
      setUnsubscribeLoading(false);
    }
  };

  const handleDeleteProgram = async () => {
    if (!program?._id) return;
    setDeleteLoading(true);
    try {
      const data = await deleteUserProgram(program._id, {
        cascadeWorkouts,
        cascadeExercises
      });
      const c = data.cascade || {};
      const w = c.workoutsDeleted ?? 0;
      const e = c.exercisesDeleted ?? 0;
      const parts = [];
      if (w) parts.push(`${w} workout${w !== 1 ? 's' : ''}`);
      if (e) parts.push(`${e} exercise${e !== 1 ? 's' : ''}`);
      toast.success(
        parts.length > 0
          ? `Program deleted. Also removed ${parts.join(' and ')} you were allowed to delete.`
          : 'Program deleted'
      );
      setDeleteModalOpen(false);
      setCascadeWorkouts(false);
      setCascadeExercises(false);
      navigate('/fitness');
    } catch (err) {
      toast.error(err.message || 'Failed to delete program');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleClone = async () => {
    try {
      if (isCoach) {
        const cloned = await cloneCoachProgram(programId);
        toast.success('Program cloned to your coach library!');
        navigate(`/coach/program/${cloned._id}/edit`);
      } else {
        const cloned = await cloneProgram(programId);
        toast.success('Program cloned! Redirecting to editor...');
        navigate(`/fitness/program/${cloned._id}/edit`);
      }
    } catch (err) {
      toast.error(err.message || 'Failed to clone program');
    }
  };

  if (loading) {
    return (
      <div className="program-page">
        <div className="program-loading">
          <div className="loading-spinner" />
          <p>Loading program...</p>
        </div>
      </div>
    );
  }

  if (error || !program) {
    return (
      <div className="program-page">
        <div className="program-error">
          <p>Error loading program: {error || 'Program not found'}</p>
          <button onClick={() => navigate('/fitness')}>Back to Programs</button>
        </div>
      </div>
    );
  }

  const canEditOwnedProgram = Boolean(
    user && !isCoach && program.userId && program.userId === user.sub && program._id
  );
  const canDeleteProgram = Boolean(
    user &&
      program._id &&
      (user.isAdmin || (!isCoach && program.userId && program.userId === user.sub))
  );

  const programJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Course',
    name: program.name,
    description: program.description || `${program.name} — multi-week fitness program`,
    url: absoluteUrl(`/fitness/program/${program._id}`),
    provider: { '@type': 'Organization', name: 'FreshFitLab', url: 'https://freshfitlab.io' },
    image: program.image,
    hasCourseInstance: {
      '@type': 'CourseInstance',
      courseMode: 'online',
      courseWorkload: program.duration ? `P${program.duration}W` : undefined,
    },
    additionalProperty: [
      ...(program.difficulty ? [{ '@type': 'PropertyValue', name: 'Difficulty', value: program.difficulty }] : []),
      ...(program.duration ? [{ '@type': 'PropertyValue', name: 'Weeks', value: program.duration }] : []),
    ],
  };

  return (
    <div className="program-page">
      <SEO
        title={`${program.name} — Multi-Week Workout Program`}
        description={
          (program.description || '').slice(0, 200) ||
          `Follow the ${program.name} program on FreshFitLab. Multi-week training plan with structured workouts, progression, and tracking.`
        }
        canonical={`/fitness/program/${program._id}`}
        image={program.image}
        type="article"
        jsonLd={[
          programJsonLd,
          buildBreadcrumbs([
            { name: 'Home', url: '/' },
            { name: 'Programs', url: '/fitness' },
            { name: program.name, url: `/fitness/program/${program._id}` },
          ]),
        ]}
      />
      {program.image && (
        <div className="program-hero-image">
          <img src={program.image} alt={`${program.name} program cover`} />
        </div>
      )}
      <div className="program-header">
        <button className="back-btn" onClick={() => navigate('/fitness')}>
          <FaArrowLeft />
          <span>All Programs</span>
        </button>

        <div className="program-header-content">
          <div className="program-badge">{program.difficulty}</div>
          <h1>{program.name}</h1>
          <p className="program-goal">{program.goal}</p>

          <div className="program-stats">
            <div className="program-stat-item">
              <span className="program-stat-value">{program.daysPerWeek}</span>
              <span className="program-stat-label">Days/Week</span>
            </div>
            <div className="program-stat-divider" />
            <div className="program-stat-item">
              <span className="program-stat-value">{program.durationWeeks}</span>
              <span className="program-stat-label">Weeks</span>
            </div>
            <div className="program-stat-divider" />
            <div className="program-stat-item">
              <span className="program-stat-value">{program.sessionDuration}</span>
              <span className="program-stat-label">Per Session</span>
            </div>
          </div>

          {user && (
            <>
              {canEditOwnedProgram && (
                <button
                  type="button"
                  className="clone-program-btn"
                  onClick={() => navigate(`/fitness/program/${program._id}/edit`)}
                >
                  <FaEdit />
                  <span>Edit program</span>
                </button>
              )}
              <button className="clone-program-btn" onClick={handleClone}>
                <FaCopy />
                <span>Clone & Customize</span>
              </button>
              <button className="clone-program-btn" onClick={() => setShowSchedule(true)}>
                <FaCalendarPlus />
                <span>Schedule</span>
              </button>
              {canDeleteProgram && (
                <button
                  type="button"
                  className="program-delete-btn"
                  onClick={() => {
                    setCascadeWorkouts(false);
                    setCascadeExercises(false);
                    setDeleteModalOpen(true);
                  }}
                >
                  <FaTrash />
                  <span>Delete</span>
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {coachingAssignment && (
        <div className="program-coach-banner">
          <p>
            This program is assigned by your coach
            {coachingAssignment.assignedBy?.name ? ` (${coachingAssignment.assignedBy.name})` : ''}.
            Hide it from My Workouts, or unsubscribe to end the connection and remove all coach assignments.
          </p>
          <div className="program-coach-banner-actions">
            <button type="button" className="program-coach-btn program-coach-btn-secondary" onClick={handleHideCoachAssignment}>
              Hide from My Workouts
            </button>
            <button type="button" className="program-coach-btn program-coach-btn-danger" onClick={() => setUnsubscribeModalOpen(true)}>
              Unsubscribe from coach
            </button>
          </div>
        </div>
      )}

      {showSchedule && program && (
        <ScheduleModal
          type="program"
          itemId={program.programId || programId}
          itemName={program.name}
          workouts={program.workouts}
          durationWeeks={program.durationWeeks || 1}
          onClose={() => setShowSchedule(false)}
        />
      )}

      <div className="program-container">
        {/* Workouts Section */}
        <section className="workouts-section">
          <h2>Weekly Workouts</h2>
          
          <div className="workouts-list">
            {program.workouts.map((workoutItem, index) => {
              const workout = workoutItem.workoutDetails;
              if (!workout) return null;

              return (
                <div 
                  key={workout.workoutId}
                  className="workout-card"
                  onClick={() => navigate(`/fitness/workout/${workout.workoutId}`)}
                >
                  <div className="workout-day">
                    <span className="day-number">Day {workoutItem.dayNumber}</span>
                    <div 
                      className="workout-category-indicator"
                      style={{ backgroundColor: getCategoryColor(workout.category) }}
                    />
                  </div>

                  <div className="workout-info">
                    <h3>{workout.name}</h3>
                    <p className="workout-description">{workout.description}</p>
                    
                    <div className="workout-meta">
                      <div className="workout-meta-item">
                        <FaClock />
                        <span>{workout.estimatedDuration} min</span>
                      </div>
                      <div className="workout-meta-item">
                        <FaDumbbell />
                        <span>{workout.totalExercises} exercises</span>
                      </div>
                    </div>

                    <div className="workout-targets">
                      {workout.targetAreas?.slice(0, 3).map((area, idx) => (
                        <span key={idx} className="target-tag">{area}</span>
                      ))}
                    </div>
                  </div>

                  <div className="workout-action">
                    <button className="view-workout-btn">
                      <FaPlay />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Guidelines Section */}
        {program.guidelines && program.guidelines.length > 0 && (
          <section className="guidelines-section">
            <h2>
              <FaExclamationTriangle className="section-icon" />
              Safety Guidelines
            </h2>
            <div className="guidelines-list">
              {program.guidelines.map((guideline, idx) => (
                <div key={idx} className="guideline-item">
                  <FaCheckCircle className="guideline-icon" />
                  <span>{guideline}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Progression Notes */}
        {program.progressionNotes && program.progressionNotes.length > 0 && (
          <section className="progression-section">
            <h2>Progression Notes</h2>
            <p className="progression-intro">After 3-4 weeks of consistent training:</p>
            <ul className="progression-list">
              {program.progressionNotes.map((note, idx) => (
                <li key={idx}>{note}</li>
              ))}
            </ul>
          </section>
        )}

        {/* Equipment Section */}
        {program.equipmentNeeded && program.equipmentNeeded.length > 0 && (
          <section className="equipment-section">
            <h2>Equipment Needed</h2>
            <div className="equipment-list">
              {program.equipmentNeeded.map((item, idx) => (
                <div key={idx} className="equipment-item">
                  <FaDumbbell className="equipment-icon" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>

      <ConfirmModal
        isOpen={deleteModalOpen}
        variant="dark"
        title="Delete this program?"
        message="This permanently removes the program from your library. Cascades only delete workouts and exercises you are allowed to remove (e.g. ones you created); anything else is skipped."
        confirmLabel="Delete program"
        cancelLabel="Cancel"
        danger
        loading={deleteLoading}
        loadingLabel="Deleting…"
        onConfirm={handleDeleteProgram}
        onCancel={() => !deleteLoading && setDeleteModalOpen(false)}
      >
        <label>
          <input
            type="checkbox"
            checked={cascadeWorkouts}
            disabled={deleteLoading}
            onChange={(e) => setCascadeWorkouts(e.target.checked)}
          />
          <span>Also delete workouts I created that this program references</span>
        </label>
        <label>
          <input
            type="checkbox"
            checked={cascadeExercises}
            disabled={deleteLoading}
            onChange={(e) => setCascadeExercises(e.target.checked)}
          />
          <span>Also delete exercises I created that appear in those program workouts</span>
        </label>
      </ConfirmModal>

      <ConfirmModal
        isOpen={unsubscribeModalOpen}
        variant="dark"
        title={`Unsubscribe from ${coachingAssignment?.assignedBy?.name || 'your coach'}?`}
        message="All workouts and programs they assigned will be removed from My Workouts."
        confirmLabel="Unsubscribe"
        cancelLabel="Cancel"
        danger
        loading={unsubscribeLoading}
        loadingLabel="Unsubscribing…"
        onConfirm={runUnsubscribeCoachFromProgram}
        onCancel={() => !unsubscribeLoading && setUnsubscribeModalOpen(false)}
      />
    </div>
  );
};

export default ProgramPage;

